import api from '@/lib/api';
import type {
  PropertyReviewStatus,
  ReviewStatus,
  LeadStatus,
  LeadPropertyType,
  BookingStatus,
  BookingSource,
  FeaturedAdminItem,
  FeaturedAdminResponse,
} from '@pms/shared';

// ─── Response shapes (mirror apps/api .../admin-platform/dto/platform-responses.ts) ──

export interface PlatformOperator {
  tenantId: string;
  name: string;
  slug: string;
  isActive: boolean;
  propertyCount: number;
  createdAt: string;
}

/**
 * A platform team member (staff login) for the super-admin Users console.
 * Mirrors PlatformUserDto in apps/api/.../admin-platform/dto/platform-responses.ts.
 * Directory + activity fields only (no password hash).
 */
export interface PlatformUser {
  id: string;
  /** Null for username-only staff (Hotel Groups). */
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  role: string;
  tenantId: string;
  operatorName: string;
  isActive: boolean;
  /** ISO timestamp of the most recent successful login, or null if never. */
  lastLoginAt: string | null;
  mustChangePassword: boolean;
  createdAt: string;
}

export interface PlatformPropertyListItem {
  id: string;
  name: string;
  city: string;
  country: string;
  operatorName: string;
  isActive: boolean;
  reviewStatus: PropertyReviewStatus;
  currency: string;
  hidden: boolean;
  createdAt: string;
}

export interface PlatformUnit {
  id: string;
  name: string;
  type: string;
  capacity: number;
  basePrice: string;
  isActive: boolean;
}

export interface PlatformPropertyDetail {
  id: string;
  name: string;
  operatorName: string;
  city: string;
  state: string | null;
  country: string;
  address: string;
  description: string | null;
  tagLine: string | null;
  currency: string;
  isActive: boolean;
  reviewStatus: PropertyReviewStatus;
  reviewFeedback: string | null;
  reviewedAt: string | null;
  submittedAt: string | null;
  amenities: string[];
  images: string[];
  policies: {
    checkInTime: string;
    checkOutTime: string;
    cancellationPolicy: string;
    petPolicy: string;
    smokingPolicy: string;
    houseRules: string | null;
  };
  units: PlatformUnit[];
  createdAt: string;
}

export interface PlatformOverview {
  operators: number;
  properties: number;
  propertiesByReviewStatus: Record<PropertyReviewStatus, number>;
  bookings: number;
  leads: number;
}

export interface PlatformLead {
  id: string;
  ownerName: string;
  email: string;
  phone: string;
  propertyName: string;
  city: string;
  country: string | null;
  propertyType: LeadPropertyType;
  roomsEstimate: number;
  message: string | null;
  status: LeadStatus;
  source: string;
  reviewFeedback: string | null;
  convertedOwnerId: string | null;
  convertedPropertyId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** POST /admin/platform/leads/:id/approve — the onboarded lead + converted ids. */
export interface ApproveLeadResult {
  lead: PlatformLead;
  ownerId: string;
  propertyId: string;
}

export interface PlatformLeadList {
  items: PlatformLead[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PlatformPropertyImages {
  id: string;
  images: string[];
}

// ─── Operator admins / team logins ─────────────────────────────────────────────
//
// POST /admin/platform/operators/:tenantId/admins mints a tenant ADMIN login,
// emails a branded invite, and returns the one-time temporary password (shown
// once). Mirrors CreateOperatorAdminDto + the controller response shape in
// apps/api/.../admin-platform.

export interface CreateOperatorAdminRequest {
  /** Optional — email OR username identifies the new login (Hotel Groups). */
  email?: string;
  /** Per-tenant login handle for email-less staff (email OR username required). */
  username?: string;
  /** Assignable role (admin | revenue_manager | operations | owner). Defaults to admin. */
  role?: string;
  firstName?: string;
  lastName?: string;
  /** Empty/omitted ⇒ all of the operator's properties (scope default). */
  propertyIds?: string[];
}

export interface OperatorAdminUser {
  id: string;
  /** Null for username-only staff (Hotel Groups). */
  email: string | null;
  /** Null for email users; set for username-only staff. */
  username: string | null;
  role: string;
  firstName: string | null;
  lastName: string | null;
  tenantId: string;
  isActive: boolean;
  mustChangePassword: boolean;
}

export interface CreateOperatorAdminResult {
  user: OperatorAdminUser;
  temporaryPassword: string;
}

/**
 * POST /admin/platform/operators/:tenantId/admins — create a tenant ADMIN login.
 * The account gets a one-time temp password (returned once, `mustChangePassword`)
 * and a branded invite email. Role is hardcoded to ADMIN server-side.
 */
export async function createOperatorAdmin(
  tenantId: string,
  body: CreateOperatorAdminRequest,
): Promise<CreateOperatorAdminResult> {
  const { data } = await api.post<CreateOperatorAdminResult>(
    `/admin/platform/operators/${tenantId}/admins`,
    body,
  );
  return data;
}

// ─── Bookings explorer (TASK-32) ───────────────────────────────────────────────

export interface MoneyByCurrency {
  currency: string;
  total: string;
  count: number;
}

export interface NamedCount {
  key: string;
  count: number;
}

export interface PlatformBookingsOverview {
  totalBookings: number;
  byStatus: Record<BookingStatus, number>;
  bySource: Record<BookingSource, number>;
  revenueByCurrency: MoneyByCurrency[];
  topOperators: NamedCount[];
  topProperties: NamedCount[];
}

/**
 * A single cross-tenant booking row for the super-admin bookings list. Mirrors
 * PlatformBookingDto in apps/api/.../admin-platform/dto/platform-responses.ts.
 * Directory + itinerary fields (guest name/email allowed — platform console);
 * no payment/folio/PIN data.
 */
export interface PlatformBooking {
  id: string;
  reference: string | null;
  guestName: string;
  guestEmail: string | null;
  propertyName: string;
  operatorName: string;
  unitName: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  adults: number | null;
  children: number | null;
  guests: number;
  status: BookingStatus;
  total: string;
  currency: string;
  source: BookingSource;
  createdAt: string;
}

export interface PlatformBookingList {
  total: number;
  rows: PlatformBooking[];
}

// ─── API functions ────────────────────────────────────────────────────────────

export async function fetchPlatformOverview(): Promise<PlatformOverview> {
  const { data } = await api.get<PlatformOverview>('/admin/platform/overview');
  return data;
}

export async function fetchPlatformOperators(): Promise<PlatformOperator[]> {
  const { data } = await api.get<PlatformOperator[]>('/admin/platform/operators');
  return data;
}

export interface UpdateOperatorRequest {
  name?: string;
  /** New login code — server-normalized; the old code stays valid as an alias. */
  groupCode?: string;
}

export interface UpdatedOperator {
  id: string;
  name: string;
  groupCode: string;
  isActive: boolean;
  retiredCodes: string[];
}

/** PATCH /admin/platform/operators/:tenantId — rename / re-code a group. */
export async function updateOperator(
  tenantId: string,
  body: UpdateOperatorRequest,
): Promise<UpdatedOperator> {
  const { data } = await api.patch<UpdatedOperator>(
    `/admin/platform/operators/${tenantId}`,
    body,
  );
  return data;
}

/** A property option in the "create group" picker. */
export interface AssignableProperty {
  id: string;
  name: string;
  city: string;
  operatorName: string;
  groupCode: string;
  /** False when the property has live bookings (can't be moved between groups). */
  movable: boolean;
}

export interface CreateOperatorRequest {
  name: string;
  groupCode: string;
  /** Optional data-free properties to place under the new group. */
  propertyIds?: string[];
}

export interface CreateOperatorResult {
  operator: { id: string; name: string; groupCode: string; isActive: boolean };
  assigned: Array<{ id: string; name: string }>;
  skipped: Array<{ id: string; name: string; reason: string }>;
}

/** GET /admin/platform/operators/assignable-properties */
export async function fetchAssignableProperties(): Promise<AssignableProperty[]> {
  const { data } = await api.get<AssignableProperty[]>(
    '/admin/platform/operators/assignable-properties',
  );
  return data;
}

/** POST /admin/platform/operators — create a new group (+ optional properties). */
export async function createOperator(
  body: CreateOperatorRequest,
): Promise<CreateOperatorResult> {
  const { data } = await api.post<CreateOperatorResult>(
    '/admin/platform/operators',
    body,
  );
  return data;
}

/** GET /admin/platform/users — every team member + operator + last-login. */
export async function fetchPlatformUsers(): Promise<PlatformUser[]> {
  const { data } = await api.get<PlatformUser[]>('/admin/platform/users');
  return data;
}

export async function fetchBookingsOverview(): Promise<PlatformBookingsOverview> {
  const { data } = await api.get<PlatformBookingsOverview>(
    '/admin/platform/bookings-overview',
  );
  return data;
}

/**
 * GET /admin/platform/bookings — the cross-tenant individual bookings list.
 * Params: limit (default 50, max 200), offset, status filter, q free-text.
 */
export async function fetchPlatformBookings(params: {
  limit?: number;
  offset?: number;
  status?: BookingStatus;
  q?: string;
}): Promise<PlatformBookingList> {
  const { data } = await api.get<PlatformBookingList>('/admin/platform/bookings', {
    params,
  });
  return data;
}

export async function fetchPlatformProperties(
  showHidden?: boolean,
): Promise<PlatformPropertyListItem[]> {
  const { data } = await api.get<PlatformPropertyListItem[]>('/admin/platform/properties', {
    ...(showHidden ? { params: { showHidden: 'true' } } : {}),
  });
  return data;
}

export async function hidePlatformProperty(
  id: string,
): Promise<PlatformPropertyDetail> {
  const { data } = await api.post<PlatformPropertyDetail>(
    `/admin/platform/properties/${id}/hide`,
  );
  return data;
}

export async function unhidePlatformProperty(
  id: string,
): Promise<PlatformPropertyDetail> {
  const { data } = await api.post<PlatformPropertyDetail>(
    `/admin/platform/properties/${id}/unhide`,
  );
  return data;
}

export async function fetchPlatformReviewQueue(
  status: PropertyReviewStatus,
): Promise<PlatformPropertyListItem[]> {
  const { data } = await api.get<PlatformPropertyListItem[]>('/admin/platform/reviews', {
    params: { status },
  });
  return data;
}

// ─── Guest-review moderation (cross-tenant, platform-scoped) ──────────────────
// Mirrors PlatformGuestReviewDto in
// apps/api/.../admin-platform/dto/platform-responses.ts.

export interface PlatformGuestReview {
  id: string;
  propertyId: string;
  propertyName: string;
  operatorName: string;
  guestName: string;
  rating: number;
  title: string | null;
  body: string | null;
  status: ReviewStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * GET /v1/admin/platform/guest-reviews?status= — cross-tenant post-stay
 * guest-review moderation queue for the super-admin console. The tenant
 * `/v1/reviews` route is fenced off from the platform role by JwtAuthGuard, so
 * the console must use this platform-scoped endpoint.
 */
export async function fetchPlatformGuestReviews(
  status?: ReviewStatus,
): Promise<PlatformGuestReview[]> {
  const { data } = await api.get<PlatformGuestReview[]>('/admin/platform/guest-reviews', {
    ...(status ? { params: { status } } : {}),
  });
  return data;
}

/**
 * POST /v1/admin/platform/guest-reviews/:id/approve — publish the review to the
 * storefront (status → approved). Returns the updated review.
 */
export async function approveGuestReview(
  id: string,
): Promise<PlatformGuestReview> {
  const { data } = await api.post<PlatformGuestReview>(
    `/admin/platform/guest-reviews/${id}/approve`,
  );
  return data;
}

/**
 * POST /v1/admin/platform/guest-reviews/:id/reject — hide the review for good
 * (status → hidden); it never surfaces publicly. Returns the updated review.
 */
export async function rejectGuestReview(
  id: string,
): Promise<PlatformGuestReview> {
  const { data } = await api.post<PlatformGuestReview>(
    `/admin/platform/guest-reviews/${id}/reject`,
  );
  return data;
}

export async function fetchPlatformPropertyDetail(
  id: string,
): Promise<PlatformPropertyDetail> {
  const { data } = await api.get<PlatformPropertyDetail>(`/admin/platform/properties/${id}`);
  return data;
}

export interface CreatePlatformPropertyRequest {
  tenantId: string;
  name: string;
  city: string;
  address: string;
  country: string;
  description?: string;
  currency?: string;
  // Rich fields carried through from the Excel importer so one create yields a
  // fully-filled draft (amenities/policies/times), not just the basics.
  amenities?: string[];
  checkInTime?: string;
  checkOutTime?: string;
  petPolicy?: string;
  website?: string;
  zipCode?: string;
  houseRules?: string;
}

// ─── Excel onboarding import (autofill "Add Property") ──────────────────────────
//
// POST /admin/platform/properties/import-preview parses an uploaded BestAcco
// onboarding .xlsx and returns a prefill for the create-property form. It does
// NOT create anything — the admin reviews it, then submits via the existing
// create flow. Mirror `PropertyImportPreview` from @pms/shared; declared locally
// until the backend type lands so the web build stays green.

export interface PropertyImportPreviewContacts {
  onSite?: string;
  email?: string;
  phone?: string;
  housekeeping?: string;
  security?: string;
  emergency?: string;
  maintenance?: string;
}

export interface PropertyImportPreviewRawEntry {
  question: string;
  answer: string;
}

export interface PropertyImportPreview {
  name?: string;
  legalEntityName?: string;
  address?: string;
  city?: string;
  country?: string;
  zipCode?: string;
  vatId?: string;
  website?: string;
  checkInTime?: string;
  checkOutTime?: string;
  petPolicy?: string;
  childrenAllowed?: boolean;
  minAgeYears?: number;
  minNights?: number;
  minAdvanceDays?: number;
  totalUnitsText?: string;
  floorsText?: string;
  amenities?: string[];
  contacts?: PropertyImportPreviewContacts;
  description?: string;
  houseRules?: string;
  raw?: PropertyImportPreviewRawEntry[];
  warnings?: string[];
}

/**
 * POST /admin/platform/properties/import-preview — upload the onboarding .xlsx
 * (multipart field name "file") and receive a review-then-submit prefill. The
 * axios xhr adapter drops the default JSON Content-Type for FormData bodies and
 * lets the browser set multipart/form-data with the correct boundary.
 */
export async function importPropertyPreview(
  file: File,
): Promise<PropertyImportPreview> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<PropertyImportPreview>(
    '/admin/platform/properties/import-preview',
    form,
  );
  return data;
}

export async function createPlatformProperty(
  body: CreatePlatformPropertyRequest,
): Promise<PlatformPropertyDetail> {
  const { data } = await api.post<PlatformPropertyDetail>(
    '/admin/platform/properties',
    body,
  );
  return data;
}

export async function approvePlatformProperty(
  id: string,
): Promise<PlatformPropertyDetail> {
  const { data } = await api.post<PlatformPropertyDetail>(
    `/admin/platform/properties/${id}/approve`,
  );
  return data;
}

export async function requestPlatformPropertyChanges(
  id: string,
  feedback: string,
): Promise<PlatformPropertyDetail> {
  const { data } = await api.post<PlatformPropertyDetail>(
    `/admin/platform/properties/${id}/request-changes`,
    { feedback },
  );
  return data;
}

// ─── Leads (TASK-59) ────────────────────────────────────────────────────────

export async function fetchPlatformLeads(params: {
  status?: LeadStatus;
  page?: number;
  pageSize?: number;
}): Promise<PlatformLeadList> {
  const { data } = await api.get<PlatformLeadList>('/admin/platform/leads', {
    params,
  });
  return data;
}

export async function updatePlatformLeadStatus(
  id: string,
  status: LeadStatus,
): Promise<PlatformLead> {
  const { data } = await api.patch<PlatformLead>(`/admin/platform/leads/${id}`, {
    status,
  });
  return data;
}

// ─── Lead decision loop (TASK-51) ─────────────────────────────────────────────

/** POST /admin/platform/leads/:id/approve — convert to Owner + draft Property. */
export async function approveLead(id: string): Promise<ApproveLeadResult> {
  const { data } = await api.post<ApproveLeadResult>(
    `/admin/platform/leads/${id}/approve`,
  );
  return data;
}

/** POST /admin/platform/leads/:id/request-changes — store feedback, → CONTACTED. */
export async function requestLeadChanges(
  id: string,
  feedback: string,
): Promise<PlatformLead> {
  const { data } = await api.post<PlatformLead>(
    `/admin/platform/leads/${id}/request-changes`,
    { feedback },
  );
  return data;
}

/** POST /admin/platform/leads/:id/reject — store reason, → CLOSED. */
export async function rejectLead(id: string, reason: string): Promise<PlatformLead> {
  const { data } = await api.post<PlatformLead>(
    `/admin/platform/leads/${id}/reject`,
    { reason },
  );
  return data;
}

// ─── Featured curation (TASK-61) ────────────────────────────────────────────

export type { FeaturedAdminItem, FeaturedAdminResponse } from '@pms/shared';

/** GET /admin/platform/featured — featured set + eligible pool + pick id. */
export async function fetchFeatured(): Promise<FeaturedAdminResponse> {
  const { data } = await api.get<FeaturedAdminResponse>('/admin/platform/featured');
  return data;
}

/** POST /admin/platform/featured/:id — feature an approved property. */
export async function featureProperty(id: string): Promise<FeaturedAdminItem> {
  const { data } = await api.post<FeaturedAdminItem>(`/admin/platform/featured/${id}`);
  return data;
}

/** DELETE /admin/platform/featured/:id — un-feature (also clears the pick). */
export async function unfeatureProperty(id: string): Promise<void> {
  await api.delete(`/admin/platform/featured/${id}`);
}

/** PUT /admin/platform/featured/order — reorder the featured set. */
export async function reorderFeatured(
  orderedIds: string[],
): Promise<FeaturedAdminResponse> {
  const { data } = await api.put<FeaturedAdminResponse>(
    '/admin/platform/featured/order',
    { orderedIds },
  );
  return data;
}

/** PUT /admin/platform/featured/:id/editors-pick — set the single global pick. */
export async function setEditorsPick(id: string): Promise<FeaturedAdminItem> {
  const { data } = await api.put<FeaturedAdminItem>(
    `/admin/platform/featured/${id}/editors-pick`,
  );
  return data;
}

// ─── Property images (TASK-60) ──────────────────────────────────────────────

export async function updatePlatformPropertyImages(
  id: string,
  images: string[],
): Promise<PlatformPropertyImages> {
  const { data } = await api.patch<PlatformPropertyImages>(
    `/admin/platform/properties/${id}/images`,
    { images },
  );
  return data;
}
